import React, { useEffect, useState, useCallback } from 'react';
import { track, EVENTS } from 'lib/analytics/analytics';
import {
  Dialog,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import apiClient from 'lib/api/apiClient';
import { SEARCH_URLS } from 'lib/api/endpoints';

/**
 * BhoonidhiSettingsDialog — the in-app Bhoonidhi credential configurator.
 *
 * Not connected → enter username + password, "Save & connect" (validates by
 * logging in; prompts OTP if the account needs one).
 * Connected → username + masked password shown read-only; "Re-verify" refreshes
 * the session with the stored password, "Disconnect" clears it. To change the
 * password, disconnect and reconnect.
 *
 * Day-to-day the auto-connect handles the session; this is only for first setup
 * or a password change.
 */
const BhoonidhiSettingsDialog = ({ open, onClose }) => {
  const isSmallScreen = useMediaQuery('(max-width:768px)');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // { connected, username, validated }
  const [step, setStep] = useState('form'); // 'form' | 'otp'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(SEARCH_URLS.bhoonidhiAccount);
      const data = res.data?.data ?? res.data;
      setStatus(data);
      if (data?.username) setUsername(data.username);
    } catch {
      setStatus(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      setStep('form');
      setPassword('');
      setOtp('');
      setPendingToken('');
      setSubmitting(false);
      setError(null);
      setNotice(null);
      loadStatus();
    }
  }, [open, loadStatus]);

  // Save (or update) creds + validate.
  const handleSave = useCallback(async () => {
    if (!username || !password) return;
    setSubmitting(true);
    setError(null);
    setNotice(null);
    track(EVENTS.BHOONIDHI_CONNECT_ATTEMPTED);
    try {
      const res = await apiClient.post(SEARCH_URLS.bhoonidhiAccountCredentials, {
        username,
        password,
      });
      const data = res.data?.data ?? res.data;
      if (data?.status === 'otp_required') {
        track(EVENTS.BHOONIDHI_OTP_REQUIRED);
        setPendingToken(data.pending_token);
        setStep('otp');
        setSubmitting(false);
        return;
      }
      track(EVENTS.BHOONIDHI_CONNECT_SUCCEEDED);
      setNotice('Connected — your Bhoonidhi account is ready.');
      setPassword('');
      setSubmitting(false);
      loadStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save. Check your username and password.');
      setSubmitting(false);
    }
  }, [username, password, loadStatus]);

  // Re-verify using the STORED password (session refresh, no re-typing).
  const handleReverify = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const res = await apiClient.post(SEARCH_URLS.bhoonidhiAccountReverify);
      const data = res.data?.data ?? res.data;
      if (data?.status === 'otp_required') {
        setPendingToken(data.pending_token);
        setStep('otp');
        setSubmitting(false);
        return;
      }
      setNotice('Re-verified — your Bhoonidhi session is fresh.');
      setSubmitting(false);
      loadStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not re-verify. Try updating your password.');
      setSubmitting(false);
    }
  }, [loadStatus]);

  const handleVerify = useCallback(async () => {
    if (otp.length !== 6) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post(SEARCH_URLS.bhoonidhiAccountVerifyOtp, {
        pending_token: pendingToken,
        otp,
      });
      track(EVENTS.BHOONIDHI_CONNECT_SUCCEEDED);
      setNotice('Connected — credentials verified and OTP received.');
      setStep('form');
      setPassword('');
      setOtp('');
      setSubmitting(false);
      loadStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Incorrect or expired code. Try again.');
      setSubmitting(false);
    }
  }, [otp, pendingToken, loadStatus]);

  const handleDisconnect = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.delete(SEARCH_URLS.bhoonidhiAccount);
      setNotice('Disconnected.');
      setUsername('');
      setPassword('');
      setSubmitting(false);
      loadStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not disconnect.');
      setSubmitting(false);
    }
  }, [loadStatus]);

  if (!open) return null;

  const fieldSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#18181F',
      border: '1px solid #2F3347',
      borderRadius: '3px',
      height: '38px',
      '& fieldset': { border: 'none' },
      '& input': {
        fontFamily: 'Poppins, sans-serif',
        fontSize: 14,
        color: '#FFF',
        '&::placeholder': { color: '#6B6E76', opacity: 1 },
      },
    },
  };
  const labelSx = { fontFamily: 'Poppins, sans-serif', fontSize: 12, color: '#C4C4C4', mb: '6px' };
  const valueSx = { fontFamily: 'Poppins, sans-serif', fontSize: 14, color: '#FFF' };
  const primaryBtnSx = {
    flex: 1,
    backgroundColor: '#4980F5',
    color: '#FFF',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 500,
    fontSize: 14,
    textTransform: 'none',
    borderRadius: '4px',
    minHeight: '40px',
    '&:hover': { backgroundColor: '#3A6FD8' },
    '&:disabled': { backgroundColor: '#3A5A9A', color: '#AAA' },
  };
  const connected = status?.connected;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      sx={{
        zIndex: 100001,
        '& .MuiDialog-container': { alignItems: 'center', justifyContent: 'center' },
      }}
      PaperProps={{
        sx: {
          width: isSmallScreen ? '92%' : '440px',
          m: 'auto',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          borderRadius: '6px',
          overflow: 'hidden',
        },
      }}
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(14, 14, 15, 0.85)', zIndex: -1 } } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: '20px',
            py: '12px',
            backgroundColor: '#2F3347',
          }}
        >
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500, fontSize: 16, color: '#FFF' }}>
            Bhoonidhi Settings
          </Typography>
          <IconButton onClick={onClose} disabled={submitting} size="small" aria-label="close" sx={{ color: '#FFF', p: 0 }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Box sx={{ backgroundColor: '#18181F', px: '20px', py: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={16} sx={{ color: '#8EB1F3' }} />
              <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 13, color: '#909296' }}>
                Loading…
              </Typography>
            </Box>
          ) : (
            <>
              {connected && (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 16, color: '#4CAF50 !important' }} />}
                  label={`Connected as ${status.username}${status.validated ? '' : ' (unverified)'}`}
                  sx={{ alignSelf: 'flex-start', bgcolor: '#1E2A1E', color: '#B7E1B7', fontFamily: 'Poppins, sans-serif', fontSize: 12 }}
                />
              )}
              {error && <Alert severity="error" sx={{ mb: 0 }}>{error}</Alert>}
              {notice && <Alert severity="success" sx={{ mb: 0 }}>{notice}</Alert>}

              {step === 'otp' ? (
                <>
                  <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 13, color: '#909296' }}>
                    A 6-digit code was emailed to your registered address. Enter it to finish.
                  </Typography>
                  <Box>
                    <Typography sx={labelSx}>Email code</Typography>
                    <TextField
                      placeholder="6-digit code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                      disabled={submitting}
                      autoFocus
                      fullWidth
                      inputProps={{ inputMode: 'numeric' }}
                      sx={fieldSx}
                    />
                  </Box>
                </>
              ) : connected ? (
                <>
                  <Box>
                    <Typography sx={labelSx}>Bhoonidhi username</Typography>
                    <Typography sx={valueSx}>{status.username}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={labelSx}>Password</Typography>
                    <Typography sx={{ ...valueSx, letterSpacing: '3px' }}>••••••••</Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, color: '#6B6E76' }}>
                    To change your password, disconnect and reconnect.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 13, color: '#909296' }}>
                    Connect your Bhoonidhi account so we can stage scenes into your cart. Your
                    password is stored encrypted; saving signs you in once to confirm it works.
                  </Typography>
                  <Box>
                    <Typography sx={labelSx}>Bhoonidhi username</Typography>
                    <TextField
                      placeholder="Your Bhoonidhi userId"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={submitting}
                      fullWidth
                      sx={fieldSx}
                    />
                  </Box>
                  <Box>
                    <Typography sx={labelSx}>Password</Typography>
                    <TextField
                      type="password"
                      placeholder="Your Bhoonidhi password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                      disabled={submitting}
                      fullWidth
                      sx={fieldSx}
                    />
                  </Box>
                </>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: '4px' }}>
                {step === 'otp' ? (
                  <Button
                    variant="contained"
                    onClick={handleVerify}
                    disabled={submitting || otp.length !== 6}
                    disableElevation
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={primaryBtnSx}
                  >
                    {submitting ? 'Verifying…' : 'Verify & Connect'}
                  </Button>
                ) : connected ? (
                  <>
                    <Button
                      variant="contained"
                      onClick={handleReverify}
                      disabled={submitting}
                      disableElevation
                      startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                      sx={primaryBtnSx}
                    >
                      {submitting ? 'Re-verifying…' : 'Re-verify'}
                    </Button>
                    <Button
                      variant="text"
                      onClick={handleDisconnect}
                      disabled={submitting}
                      sx={{ color: '#DD4A4A', fontFamily: 'Poppins, sans-serif', fontSize: 13, textTransform: 'none' }}
                    >
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={submitting || !username || !password}
                    disableElevation
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={primaryBtnSx}
                  >
                    {submitting ? 'Saving…' : 'Save & connect'}
                  </Button>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default BhoonidhiSettingsDialog;
